/**
 * Datos de la regularización en Staff (007 · T032 y T056; contracts/staff-api.md, contracts/ui.md
 * §1.1, research R-9 y R-16).
 *
 * - `GET /attendance/regularizations` (`useStaffRegularizations`): huecos y solicitudes del
 *   trabajador, ya ordenados y decididos por el servidor (`can_request`, `bounds`): aquí no se
 *   calcula ningún hueco, límite ni permiso (FR-003). `refetchOnWindowFocus: true` como el
 *   historial (volver a la PWA refresca); `staleTime` el global.
 * - `POST /attendance/regularizations` (`useCreateRegularization`) y
 *   `POST /attendance/regularizations/{id}/cancel` (`useCancelRegularization`): sin optimistic UI;
 *   al terminar se invalidan estado, historial y regularizaciones (`invalidateAttendanceViews`).
 * - `newIdempotencyKey()`: una por intento de envío; la pantalla la conserva entre reintentos del
 *   mismo contenido y la renueva al cambiar día, hora o motivo (R-9).
 *
 * Todas son rutas **autenticadas** del realm staff: el cliente firma `X-Fermin-Device` y añade la
 * cabecera anti-CSRF en las mutaciones. Ningún motivo, nota ni hora se loguea.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  RegularizationUnavailableReasonSchema,
  StaffRegularizationCancelResponseSchema,
  StaffRegularizationMutationResponseSchema,
  StaffRegularizationsResponseSchema,
  type CreateStaffRegularizationBody,
  type StaffRegularizationCancelResponse,
  type StaffRegularizationItem,
  type StaffRegularizationMutationResponse,
  type StaffRegularizationsResponse,
} from '@fermin/contracts';

import { api, ApiError } from '../../api/client';
import { t } from '../../i18n';
import { invalidateAttendanceViews } from '../history/api';

import { STAFF_REGULARIZATIONS_QUERY_KEY } from './keys';

export { STAFF_REGULARIZATIONS_QUERY_KEY };

export const REGULARIZATIONS_PATH = '/attendance/regularizations';

export function fetchStaffRegularizations(signal?: AbortSignal): Promise<StaffRegularizationsResponse> {
  return api.get(REGULARIZATIONS_PATH, {
    schema: StaffRegularizationsResponseSchema,
    ...(signal ? { signal } : {}),
  });
}

export interface StaffRegularizationsQueryOptions {
  enabled?: boolean;
}

/** Huecos y solicitudes del trabajador (≤ 100, en el orden del servidor). */
export function useStaffRegularizations(
  options: StaffRegularizationsQueryOptions = {},
): UseQueryResult<StaffRegularizationsResponse, ApiError> {
  return useQuery<StaffRegularizationsResponse, ApiError>({
    queryKey: STAFF_REGULARIZATIONS_QUERY_KEY,
    queryFn: ({ signal }) => fetchStaffRegularizations(signal),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: true,
  });
}

/** UUID v4 por intento de envío (`IdempotencyKeySchema = z.uuid()`). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Sin `randomUUID` (WebView antigua): UUID v4 con `getRandomValues`.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createRegularization(
  body: CreateStaffRegularizationBody,
): Promise<StaffRegularizationMutationResponse> {
  return api.post(REGULARIZATIONS_PATH, {
    body,
    schema: StaffRegularizationMutationResponseSchema,
  });
}

/**
 * Envía una propuesta. Sin optimistic UI: la pantalla espera la respuesta. Tras un `201`/`200`, y
 * también tras un `409 REGULARIZATION_UNAVAILABLE` (la situación cambió en el servidor), se
 * invalidan las vistas de asistencia.
 */
export function useCreateRegularization(): UseMutationResult<
  StaffRegularizationMutationResponse,
  ApiError,
  CreateStaffRegularizationBody
> {
  const queryClient = useQueryClient();
  return useMutation<StaffRegularizationMutationResponse, ApiError, CreateStaffRegularizationBody>({
    mutationFn: createRegularization,
    onSuccess: async () => {
      await invalidateAttendanceViews(queryClient);
    },
    onError: async (error) => {
      if (isStaleStateError(error)) {
        await invalidateAttendanceViews(queryClient);
      }
    },
  });
}

export function cancelRegularization(id: string): Promise<StaffRegularizationCancelResponse> {
  return api.post(`${REGULARIZATIONS_PATH}/${encodeURIComponent(id)}/cancel`, {
    body: {},
    schema: StaffRegularizationCancelResponseSchema,
  });
}

/** Cancela una solicitud `PENDING` propia (cuenta como propuesta usada, FR-009). */
export function useCancelRegularization(): UseMutationResult<
  StaffRegularizationCancelResponse,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation<StaffRegularizationCancelResponse, ApiError, string>({
    mutationFn: cancelRegularization,
    onSuccess: async () => {
      await invalidateAttendanceViews(queryClient);
    },
    onError: async (error) => {
      if (isStaleStateError(error)) {
        await invalidateAttendanceViews(queryClient);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Selecciones puras sobre la respuesta (sin reglas: solo leen lo que decidió el servidor)
// ---------------------------------------------------------------------------

/** Ítems en los que el trabajador puede actuar (`can_request`), en el orden del servidor. */
export function actionableItems(
  items: readonly StaffRegularizationItem[],
): StaffRegularizationItem[] {
  return items.filter((item) => item.can_request);
}

/** Índice por `anchor.mark_id` (un ancla aparece una sola vez en la respuesta). */
export function itemsByAnchor(
  items: readonly StaffRegularizationItem[],
): Map<string, StaffRegularizationItem> {
  return new Map(items.map((item) => [item.anchor.mark_id, item]));
}

// ---------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------

/** El servidor dice que la situación ya cambió: hay que recargar, no reintentar. */
export function isStaleStateError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.is('REGULARIZATION_UNAVAILABLE') ||
      error.is('REGULARIZATION_NOT_OPEN') ||
      error.is('NOT_FOUND'))
  );
}

/** Texto es-PE de un `REGULARIZATION_UNAVAILABLE`/`NOT_OPEN`/`NOT_FOUND`, por `details.reason`. */
export function unavailableMessage(error: ApiError): string {
  if (error.is('REGULARIZATION_NOT_OPEN')) {
    return t('regularization.errors.REGULARIZATION_NOT_OPEN');
  }
  if (error.is('NOT_FOUND')) {
    return t('regularization.unavailable.NOT_FOUND');
  }
  const reason = RegularizationUnavailableReasonSchema.safeParse(error.details?.reason);
  return reason.success
    ? t(`regularization.unavailable.${reason.data}`)
    : t('regularization.errors.REGULARIZATION_UNAVAILABLE');
}

export type RegularizationField = 'date' | 'time' | 'reason';

export type RegularizationFieldErrors = Partial<Record<RegularizationField, string>>;

const FIELDS: readonly RegularizationField[] = ['date', 'time', 'reason'];

/**
 * `400 VALIDATION_ERROR` → mensaje por campo (`details.issues[].path` = `date`/`time`/`reason`,
 * con el texto es-PE que ya arma el servidor). `null` si el error no trae campos reconocibles.
 */
export function fieldErrorsOf(error: ApiError): RegularizationFieldErrors | null {
  if (!error.is('VALIDATION_ERROR')) {
    return null;
  }
  const issues = error.details?.issues;
  if (!Array.isArray(issues)) {
    return null;
  }
  const out: RegularizationFieldErrors = {};
  for (const issue of issues as unknown[]) {
    if (typeof issue !== 'object' || issue === null) {
      continue;
    }
    const { path, message } = issue as { path?: unknown; message?: unknown };
    const field = typeof path === 'string' ? path.split('.').at(-1) : undefined;
    if (
      field !== undefined &&
      (FIELDS as readonly string[]).includes(field) &&
      typeof message === 'string' &&
      out[field as RegularizationField] === undefined
    ) {
      out[field as RegularizationField] = message;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}
